import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, User, Tag, ArrowRight, Plus, Trash2, Edit, Image, Video, Upload, X, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const { user, isAuthenticated } = useAuthStore();
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    tags: '',
    featured_image: '',
    media_urls: [],
    video_url: ''
  });
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);

  const [hasArtistProfile, setHasArtistProfile] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isOwner = user?.role === 'owner';
  const isOverseer = user?.role === 'overseer';
  const isArtist = user?.role === 'artist' || hasArtistProfile;
  const canPost = isAdmin || isOwner || isOverseer || isArtist;

  useEffect(() => {
    fetchPosts();
    if (isAuthenticated) {
      checkArtistProfile();
    }
  }, [selectedTag, isAuthenticated]);

  const checkArtistProfile = async () => {
    try {
      const response = await api.get('/artist/profile');
      if (response.data && response.data.status === 'approved') {
        setHasArtistProfile(true);
      }
    } catch (error) {
      // User doesn't have an artist profile, that's fine
    }
  };

  const fetchPosts = async () => {
    try {
      const url = selectedTag ? `/blog?tag=${selectedTag}` : '/blog';
      const response = await api.get(url);
      setPosts(response.data);
    } catch (error) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Compress and resize image before converting to base64
  const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (let file of files) {
      // Check original file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB.`);
        continue;
      }

      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }

      try {
        // Upload file to server
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'blog');
        
        const response = await api.post('/upload-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = response.data.url;
        setNewPost(prev => ({ ...prev, media_urls: [...prev.media_urls, imageUrl] }));
        toast.success(`${file.name} uploaded!`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Server error'}`);
      }
    }
    setUploading(false);
  };

  // Parse video URL to get embed URL
  const parseVideoUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`, videoId: ytMatch[1] };
    }
    
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`, videoId: vimeoMatch[1] };
    }
    
    // TikTok
    const tiktokMatch = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
    if (tiktokMatch) {
      return { type: 'tiktok', embedUrl: `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}`, videoId: tiktokMatch[1] };
    }
    
    return null;
  };

  const handleVideoUrlChange = (url) => {
    const parsed = parseVideoUrl(url);
    if (url && !parsed) {
      // Still save the URL, validation happens on submit
      setNewPost(prev => ({ ...prev, video_url: url }));
    } else {
      setNewPost(prev => ({ ...prev, video_url: url }));
    }
  };

  const getVideoPreview = () => {
    const parsed = parseVideoUrl(newPost.video_url);
    if (!parsed) return null;
    return parsed;
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const removeVideo = () => {
    setNewPost(prev => ({ ...prev, video_url: '' }));
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      const postData = {
        title: newPost.title,
        excerpt: newPost.excerpt || newPost.content.substring(0, 150) + '...',
        content: newPost.content,
        tags: newPost.tags.split(',').map(t => t.trim()).filter(t => t),
        featured_image: newPost.featured_image || newPost.media_urls[0] || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
        media_urls: newPost.media_urls,
        video_url: newPost.video_url
      };
      await api.post('/blog', postData);
      toast.success('Blog post created!');
      setShowCreate(false);
      setNewPost({ title: '', excerpt: '', content: '', tags: '', featured_image: '', media_urls: [], video_url: '' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await api.delete(`/blog/${postId}`);
      toast.success('Blog post deleted');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const allTags = [...new Set(posts.flatMap(p => p.tags || []))];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-10 h-10 text-purple-400" />
              <h1 className="text-4xl font-bold text-white">Blog & News</h1>
            </div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Stay updated with the latest tips, trends, and news from the music booking industry
            </p>
          </div>
          {canPost && (
            <Button onClick={() => setShowCreate(true)} className="ml-4">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          )}
        </div>

        {/* Role Notice */}
        {canPost && (
          <div className={`${isAdmin ? 'bg-purple-500/20 border-purple-500/30' : 'bg-blue-500/20 border-blue-500/30'} border rounded-lg p-4 mb-6`}>
            <p className={`${isAdmin ? 'text-purple-300' : 'text-blue-300'} text-sm`}>
              <strong>{isAdmin ? 'Admin Mode:' : 'Artist Mode:'}</strong> You can create blog posts with photos and videos.
              {isAdmin && ' You can also delete any post by clicking the trash icon.'}
            </p>
          </div>
        )}

        {/* Tags Filter */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                !selectedTag ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${
                  selectedTag === tag ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Create Post Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-white mb-4">Create Blog Post</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                  <Input
                    value={newPost.title}
                    onChange={e => setNewPost({ ...newPost, title: e.target.value })}
                    placeholder="Enter post title"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Excerpt (optional)</label>
                  <Input
                    value={newPost.excerpt}
                    onChange={e => setNewPost({ ...newPost, excerpt: e.target.value })}
                    placeholder="Brief description for preview"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Content *</label>
                  <textarea
                    value={newPost.content}
                    onChange={e => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Write your blog post content here..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white min-h-[150px]"
                  />
                </div>

                {/* Media Upload Section */}
                <div className="space-y-4">
                  <label className="text-sm text-gray-400 block">Photos & Videos</label>
                  
                  {/* Photo Upload */}
                  <div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full border-dashed border-gray-600 hover:border-purple-500"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      {uploading ? 'Processing...' : 'Add Photos from Device'}
                    </Button>
                  </div>

                  {uploading && (
                    <div className="flex items-center gap-2 text-purple-400">
                      <Upload className="w-4 h-4 animate-bounce" />
                      <span className="text-sm">Compressing and uploading...</span>
                    </div>
                  )}

                  {/* Image Previews */}
                  {newPost.media_urls.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {newPost.media_urls.map((url, index) => {
                        // Handle both base64 and API URLs
                        const imgSrc = url.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${url}` 
                          : url;
                        return (
                          <div key={index} className="relative group">
                            <img
                              src={imgSrc}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Video URL Input */}
                  <div className="space-y-2">
                    <label className="text-sm text-gray-400 block flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Video Link (YouTube, Vimeo, or TikTok)
                    </label>
                    <Input
                      value={newPost.video_url}
                      onChange={e => handleVideoUrlChange(e.target.value)}
                      placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                      className="bg-gray-800 border-gray-700"
                    />
                    
                    {/* Video Preview */}
                    {newPost.video_url && getVideoPreview() && (
                      <div className="relative rounded-lg overflow-hidden bg-black">
                        <iframe
                          src={getVideoPreview().embedUrl}
                          className="w-full aspect-video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Video preview"
                        />
                        <button
                          onClick={removeVideo}
                          className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    )}
                    
                    {newPost.video_url && !getVideoPreview() && (
                      <p className="text-yellow-400 text-xs flex items-center gap-1">
                        ⚠️ Please enter a valid YouTube, Vimeo, or TikTok URL
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    📷 Photos: Auto-compressed to max 2MB • Supported: JPG, PNG, WebP
                    <br />
                    🎥 Videos: Paste a YouTube, Vimeo, or TikTok link to embed
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Tags (comma separated)</label>
                  <Input
                    value={newPost.tags}
                    onChange={e => setNewPost({ ...newPost, tags: e.target.value })}
                    placeholder="tips, music, events"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={createPost} className="flex-1" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Publish Post'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl">
            <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No posts yet. Check back soon!</p>
            {canPost && (
              <Button onClick={() => setShowCreate(true)} className="mt-4">
                Create the first post
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, index) => {
              // Get YouTube thumbnail if available
              const getVideoThumbnail = (url) => {
                if (!url) return null;
                const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                if (ytMatch) {
                  return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
                }
                return null;
              };
              
              // Get proper image URL (handle relative API URLs)
              const getImageUrl = (url) => {
                if (!url) return null;
                if (url.startsWith('/api')) {
                  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
                }
                return url;
              };
              
              const videoThumbnail = getVideoThumbnail(post.video_url);
              const featuredImageUrl = getImageUrl(post.featured_image) || getImageUrl(post.media_urls?.[0]) || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800';
              
              return (
                <div
                  key={post.post_id}
                  className="glass-default rounded-xl overflow-hidden hover-lift border border-white/10"
                >
                  {/* Featured Image or Video Thumbnail */}
                  <div className="relative h-48 overflow-hidden">
                    {post.video_url && videoThumbnail ? (
                      <div className="relative w-full h-full bg-black">
                        <img
                          src={videoThumbnail}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                            <Play className="w-6 h-6 text-white ml-1" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={featuredImageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {post.tags?.length > 0 && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-purple-500/80 text-white px-3 py-1 rounded-full text-xs capitalize">
                          {post.tags[0]}
                        </span>
                      </div>
                    )}
                    {/* Media indicators */}
                    <div className="absolute bottom-3 right-3 flex gap-2">
                      {post.media_urls?.length > 1 && (
                        <span className="bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {post.media_urls.length}
                        </span>
                      )}
                      {post.video_url && (
                        <span className="bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                          <Video className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Recently'}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author_name || 'RazientSounds'}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-3 mb-4">{post.excerpt}</p>
                    
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/blog/${post.slug || post.post_id}`}
                        className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center gap-1"
                      >
                        Read More <ArrowRight className="w-4 h-4" />
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => deletePost(post.post_id)}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
